import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderOpen, ChevronRight, Search } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import { estadoLabel } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function DashboardPage() {
  const nav = useNavigate();
  const [ops, setOps] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/operaciones");
      setOps(data);
    } catch (e) {
      toast.error("No se pudieron cargar las operaciones");
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!titulo.trim()) return toast.error("Nombra la operación");
    setCreating(true);
    try {
      const { data } = await api.post("/operaciones", { titulo });
      toast.success("Operación creada");
      setOpen(false); setTitulo("");
      nav(`/app/operaciones/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear");
    } finally { setCreating(false); }
  };

  const filtered = ops.filter((o) =>
    o.titulo.toLowerCase().includes(q.toLowerCase()) ||
    (o.notas || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900">Tus operaciones</h1>
            <p className="text-slate-600 mt-1">Cada operación agrupa los documentos de un caso.</p>
          </div>
          <button onClick={() => setOpen(true)} className="bm-btn-primary" data-testid="new-operation-btn">
            <Plus className="w-4 h-4" /> Nueva operación
          </button>
        </div>

        <div className="mb-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dashboard-search"
                 placeholder="Buscar operación…"
                 className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#E2DCD2] bg-white outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
        </div>

        {filtered.length === 0 ? (
          <div className="bm-card p-10 text-center" data-testid="dashboard-empty">
            <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-heading font-bold text-slate-900">Aún no tienes operaciones</h3>
            <p className="text-slate-600 mt-1 mb-5">Crea la primera para empezar a generar documentos.</p>
            <button onClick={() => setOpen(true)} className="bm-btn-primary" data-testid="dashboard-empty-cta">
              <Plus className="w-4 h-4" /> Crear operación
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((op) => (
              <button key={op.id} onClick={() => nav(`/app/operaciones/${op.id}`)}
                      data-testid={`op-card-${op.id}`}
                      className="bm-card p-5 text-left group">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`bm-badge bm-badge-${op.estado}`}>{estadoLabel[op.estado] || op.estado}</span>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-slate-900 line-clamp-2">{op.titulo}</h3>
                    {op.notas && <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{op.notas}</p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#1E3A5F] transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-operation-dialog">
          <DialogHeader>
            <DialogTitle>Nueva operación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-700">Nombre de la operación</label>
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-testid="new-op-title-input"
                   className="w-full h-12 px-4 rounded-xl border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                   placeholder="Ej. Depto Roma Norte — Familia López" autoFocus />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="text-slate-600 px-4 py-2" data-testid="new-op-cancel">Cancelar</button>
            <button onClick={create} disabled={creating} className="bm-btn-primary" data-testid="new-op-create">Crear</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
